
import React, { useState, useEffect, useCallback, useRef } from 'react';

const GRID_SIZE = 20;
const INITIAL_SNAKE = [[10, 10], [10, 11], [10, 12]];
const INITIAL_DIRECTION = [0, -1];

const Snake: React.FC = () => {
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [food, setFood] = useState([5, 5]);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);

  const moveSnake = useCallback(() => {
    const newSnake = [...snake];
    const head = [newSnake[0][0] + direction[0], newSnake[0][1] + direction[1]];

    if (head[0] < 0 || head[0] >= GRID_SIZE || head[1] < 0 || head[1] >= GRID_SIZE) {
      setGameOver(true);
      return;
    }

    if (newSnake.some(segment => segment[0] === head[0] && segment[1] === head[1])) {
      setGameOver(true);
      return;
    }

    newSnake.unshift(head);

    if (head[0] === food[0] && head[1] === food[1]) {
      setScore(s => s + 10);
      setFood([Math.floor(Math.random() * GRID_SIZE), Math.floor(Math.random() * GRID_SIZE)]);
    } else {
      newSnake.pop();
    }

    setSnake(newSnake);
  }, [snake, direction, food]);

  useEffect(() => {
    if (gameOver) return;
    const interval = setInterval(moveSnake, 120);
    return () => clearInterval(interval);
  }, [moveSnake, gameOver]);

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp': if (direction[1] !== 1) setDirection([0, -1]); break;
        case 'ArrowDown': if (direction[1] !== -1) setDirection([0, 1]); break;
        case 'ArrowLeft': if (direction[0] !== 1) setDirection([-1, 0]); break;
        case 'ArrowRight': if (direction[0] !== -1) setDirection([1, 0]); break;
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [direction]);

  const reset = () => {
    setSnake(INITIAL_SNAKE);
    setDirection(INITIAL_DIRECTION);
    setGameOver(false);
    setScore(0);
  };

  return (
    <div className="flex flex-col items-center game-container">
      <div className="mb-6 flex justify-between w-full max-w-md items-center glass p-4 rounded-2xl">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Session Score</span>
          <span className="text-2xl font-black text-green-400 font-mono">{score.toString().padStart(6, '0')}</span>
        </div>
        {gameOver && (
          <button onClick={reset} className="bg-green-600 hover:bg-green-500 px-6 py-2 rounded-xl font-bold transition-all shadow-lg shadow-green-500/20 active:scale-95">
            REBOOT
          </button>
        )}
      </div>
      
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
        <div className="relative grid bg-slate-900/90 rounded-xl overflow-hidden border-2 border-slate-800 shadow-2xl" 
             style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`, width: 'min(90vw, 400px)', height: 'min(90vw, 400px)' }}>
          {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, i) => {
            const x = i % GRID_SIZE;
            const y = Math.floor(i / GRID_SIZE);
            const isHead = snake[0][0] === x && snake[0][1] === y;
            const isSnake = snake.some(s => s[0] === x && s[1] === y);
            const isFood = food[0] === x && food[1] === y;
            
            return (
              <div 
                key={i} 
                className={`w-full h-full transition-all duration-300 ${
                  isHead ? 'bg-green-300 scale-110 z-10 rounded-sm shadow-[0_0_15px_rgba(110,231,183,0.8)]' : 
                  isSnake ? 'bg-green-500 scale-95 rounded-sm shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 
                  isFood ? 'bg-red-500 rounded-full scale-75 animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.6)]' : 
                  'bg-transparent'
                }`}
              />
            );
          })}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-3 md:hidden">
        <div />
        <button onTouchStart={() => direction[1] !== 1 && setDirection([0, -1])} className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-2xl">↑</button>
        <div />
        <button onTouchStart={() => direction[0] !== 1 && setDirection([-1, 0])} className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-2xl">←</button>
        <button onTouchStart={() => direction[1] !== -1 && setDirection([0, 1])} className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-2xl">↓</button>
        <button onTouchStart={() => direction[0] !== -1 && setDirection([1, 0])} className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-2xl">→</button>
      </div>
    </div>
  );
};

export default Snake;
